var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import noop from 'lodash/noop';
import uniqueId from 'lodash/uniqueId';
import { CHILDREN_ENDPOINT_ACTION } from '../contants';
import TvingFocusable from '../TvingFocusable';
import { NavigationType } from '../types';
import { useFocusContext, useFocusRoot } from './useFocusedContext';
var useFocusableHook = function (_a) {
    var _b = _a === void 0 ? {} : _a, _c = _b.focusable, focusable = _c === void 0 ? true : _c, _d = _b.saveLastFocusedChild, saveLastFocusedChild = _d === void 0 ? true : _d, _e = _b.trackChildren, trackChildren = _e === void 0 ? false : _e, _f = _b.autoRestoreFocus, autoRestoreFocus = _f === void 0 ? true : _f, _g = _b.isInactive, isInactive = _g === void 0 ? false : _g, _h = _b.isFocusBoundary, isFocusBoundary = _h === void 0 ? false : _h, propFocusKey = _b.focusKey, _j = _b.navigationType, navigationType = _j === void 0 ? NavigationType.SPATIAL : _j, focusIndex = _b.focusIndex, preferredChildFocusKey = _b.preferredChildFocusKey, _k = _b.onChildrenEndPointEvent, onChildrenEndPointEvent = _k === void 0 ? noop : _k, _l = _b.onEnterPress, onEnterPress = _l === void 0 ? noop : _l, _m = _b.onEnterRelease, onEnterRelease = _m === void 0 ? noop : _m, _o = _b.onArrowPress, onArrowPress = _o === void 0 ? function () { return true; } : _o, onBackPress = _b.onBackPress, _p = _b.onFocus, onFocus = _p === void 0 ? noop : _p, _q = _b.onBlur, onBlur = _q === void 0 ? noop : _q, extraProps = _b.extraProps, _r = _b.childrenEndPointAction, childrenEndPointAction = _r === void 0 ? CHILDREN_ENDPOINT_ACTION.NONE : _r, _s = _b.overlapChildren, overlapChildren = _s === void 0 ? false : _s;
    var onEnterPressHandler = useCallback(function (details) {
        onEnterPress(extraProps || {}, details);
    }, [onEnterPress, extraProps]);
    var onEnterReleaseHandler = useCallback(function () {
        onEnterRelease(extraProps || {});
    }, [onEnterRelease, extraProps]);
    var onArrowPressHandler = useCallback(function (direction, details) { return onArrowPress(direction, extraProps || {}, details); }, [extraProps, onArrowPress]);
    var onFocusHandler = useCallback(function (layout, details) {
        onFocus(layout, extraProps || {}, details);
    }, [extraProps, onFocus]);
    var onBlurHandler = useCallback(function (layout, details) {
        onBlur(layout, extraProps || {}, details);
    }, [extraProps, onBlur]);
    var onChildrenEndPointEventHandler = useCallback(function (direction, details) { return onChildrenEndPointEvent(direction, details); }, [onChildrenEndPointEvent]);
    var ref = useRef(null);
    var _t = __read(useState(false), 2), focused = _t[0], setFocused = _t[1];
    var _u = __read(useState(false), 2), hasFocusedChild = _u[0], setHasFocusedChild = _u[1];
    var rootFocusKey = useFocusRoot();
    if (!rootFocusKey) {
        throw new Error('useFocusable must be used inside a FocusRoot');
    }
    var tvingFocusable = TvingFocusable.getInstance(rootFocusKey);
    var parentFocusKey = useFocusContext();
    /**
     * Either using the propFocusKey passed in, or generating a random one
     */
    var focusKey = useMemo(function () { return propFocusKey || uniqueId('TVING_'); }, [propFocusKey]);
    var focusSelf = useCallback(function () {
        tvingFocusable.setFocus(focusKey);
    }, [focusKey]);
    useEffect(function () {
        var node = ref.current;
        if (node) {
            tvingFocusable.addFocusable({
                focusKey: focusKey,
                navigationType: navigationType,
                focusIndex: focusIndex,
                node: node,
                parentFocusKey: parentFocusKey,
                preferredChildFocusKey: preferredChildFocusKey,
                onEnterPress: onEnterPressHandler,
                onEnterRelease: onEnterReleaseHandler,
                onArrowPress: onArrowPressHandler,
                onBackPress: onBackPress,
                onFocus: onFocusHandler,
                onBlur: onBlurHandler,
                onUpdateFocus: function (isFocused) {
                    if (isFocused === void 0) { isFocused = false; }
                    return setFocused(isFocused);
                },
                onUpdateHasFocusedChild: function (isFocused) {
                    if (isFocused === void 0) { isFocused = false; }
                    return setHasFocusedChild(isFocused);
                },
                saveLastFocusedChild: saveLastFocusedChild,
                trackChildren: trackChildren,
                isInactive: isInactive,
                isFocusBoundary: isFocusBoundary,
                onChildrenEndPointEvent: onChildrenEndPointEventHandler,
                autoRestoreFocus: autoRestoreFocus,
                focusable: focusable,
                childrenEndPointAction: childrenEndPointAction,
                overlapChildren: overlapChildren,
            });
        }
        return function () {
            tvingFocusable.removeFocusable({
                focusKey: focusKey,
            });
        };
    }, []);
    useEffect(function () {
        var node = ref.current;
        if (node) {
            tvingFocusable.updateFocusable(focusKey, {
                node: node,
                preferredChildFocusKey: preferredChildFocusKey,
                focusable: focusable,
                isFocusBoundary: isFocusBoundary,
                onEnterPress: onEnterPressHandler,
                onEnterRelease: onEnterReleaseHandler,
                onArrowPress: onArrowPressHandler,
                onBackPress: onBackPress,
                onFocus: onFocusHandler,
                onBlur: onBlurHandler,
                onChildrenEndPointEvent: onChildrenEndPointEventHandler,
            });
        }
    }, [
        focusKey,
        preferredChildFocusKey,
        focusable,
        isFocusBoundary,
        onEnterPressHandler,
        onEnterReleaseHandler,
        onArrowPressHandler,
        onBackPress,
        onFocusHandler,
        onBlurHandler,
        onChildrenEndPointEventHandler,
    ]);
    var tvingFocusableRef = useRef(tvingFocusable);
    return {
        ref: ref,
        focusSelf: focusSelf,
        focused: focused,
        hasFocusedChild: hasFocusedChild,
        focusKey: focusKey,
        focusIndex: focusIndex,
        parentFocusKey: parentFocusKey,
        setFocus: tvingFocusable.isNativeMode() ? noop : tvingFocusable.setFocus.bind(tvingFocusable),
        getComponentByFocusKey: tvingFocusable.getComponentByFocusKey.bind(tvingFocusable),
        getCurrentFocus: tvingFocusable.getCurrentFocus.bind(tvingFocusable),
        navigateByDirection: tvingFocusable.navigateByDirection.bind(tvingFocusable),
        pause: tvingFocusable.pause.bind(tvingFocusable),
        resume: tvingFocusable.resume.bind(tvingFocusable),
        updateAllLayouts: tvingFocusable.updateAllLayouts.bind(tvingFocusable),
        focusable: focusable,
        tvingFocusableRef: tvingFocusableRef,
        keyDownEventListener: tvingFocusable.keyDownEventListener.bind(tvingFocusable),
        keyUpEventListener: tvingFocusable.keyUpEventListener.bind(tvingFocusable),
    };
};
export var useFocusable = useFocusableHook;
